/**
 * Chart Timeframe Configurations
 *
 * Defines available timeframes for stock charts with Polygon.io aggregates API
 * Each timeframe specifies how to fetch and display historical data
 */

export type ChartTimeframe = '1D' | '1W' | '1M' | '3M' | '1Y' | '5Y';

export interface ChartTimeframeConfig {
  label: string;
  days: number; // Number of days to go back
  multiplier: number; // Polygon API multiplier param
  timespan: 'minute' | 'hour' | 'day' | 'week'; // Polygon API timespan param
  cacheTTL: number; // Cache time-to-live in milliseconds
}

/**
 * Timeframe configurations optimized for:
 * - Readable charts (~90-250 data points)
 * - Polygon Starter plan (5 years history, unlimited calls)
 * - 180px chart height (existing design)
 */
export const CHART_TIMEFRAMES: Record<ChartTimeframe, ChartTimeframeConfig> = {
  '1D': {
    label: '1D',
    days: 1,
    multiplier: 5,
    timespan: 'minute',
    cacheTTL: 5 * 60 * 1000, // 5 minutes
  },
  '1W': {
    label: '1W',
    days: 7,
    multiplier: 30,
    timespan: 'minute',
    cacheTTL: 5 * 60 * 1000, // 5 minutes
  },
  '1M': {
    label: '1M',
    days: 30,
    multiplier: 2,
    timespan: 'hour',
    cacheTTL: 30 * 60 * 1000, // 30 minutes
  },
  '3M': {
    label: '3M',
    days: 90,
    multiplier: 1,
    timespan: 'day',
    cacheTTL: 30 * 60 * 1000, // 30 minutes
  },
  '1Y': {
    label: '1Y',
    days: 365,
    multiplier: 1,
    timespan: 'day',
    cacheTTL: 2 * 60 * 60 * 1000, // 2 hours
  },
  '5Y': {
    label: '5Y',
    days: 365 * 5,
    multiplier: 1,
    timespan: 'week',
    cacheTTL: 2 * 60 * 60 * 1000, // 2 hours
  },
};

/**
 * Default timeframe when opening a chart
 */
export const DEFAULT_TIMEFRAME: ChartTimeframe = '1D';

/**
 * All available timeframes in display order
 */
export const TIMEFRAME_OPTIONS: ChartTimeframe[] = ['1D', '1W', '1M', '3M', '1Y', '5Y'];
